package com.tovtalk.data.remote.dto

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonObject

/** 403/4xx error body from API (e.g. detail + code KASHRUT_NOT_ALLOWED) */
@Serializable
data class ApiErrorBody(
    @SerialName("detail") val detail: String? = null,
    @SerialName("code") val code: String? = null
)

/** Response for POST /users/me/device-token – API returns data: { "device": "ios" } only */
@Serializable
data class DeviceTokenResponse(
    @SerialName("status") val status: Boolean = true,
    @SerialName("message") val message: String? = null,
    @SerialName("data") val data: DeviceTokenData? = null
)

@Serializable
data class DeviceTokenData(
    @SerialName("device") val device: String? = null
)

/** Response for DELETE /auth/delete-account – success returns data: {} */
@Serializable
data class DeleteAccountResponse(
    @SerialName("status") val status: Boolean = true,
    @SerialName("message") val message: String? = null,
    @SerialName("data") val data: JsonObject? = null
)

/** Response for GET /users/me/firebase-custom-token (session restore for Firestore/chat) */
@Serializable
data class FirebaseCustomTokenResponse(
    @SerialName("status") val status: Boolean = true,
    @SerialName("message") val message: String? = null,
    @SerialName("data") val data: FirebaseCustomTokenData? = null
)

@Serializable
data class FirebaseCustomTokenData(
    @SerialName("firebase_custom_token") val firebaseCustomToken: String? = null
)

/** Wrapper for single-user V2 responses: {"data": {"user": {...}}} */
@Serializable
data class V2UserData(
    @SerialName("user") val user: UserDto
)

@Serializable
data class UserDto(
    @SerialName("id") val id: Int,
    @SerialName("firebase_uid") val firebaseUid: String? = null,
    @SerialName("identity") val identity: String? = null,
    @SerialName("fullname") val fullname: String,
    @SerialName("username") val username: String,
    @SerialName("user_email") val userEmail: String? = null,
    @SerialName("profile_photo") val profilePhoto: String? = null,
    @SerialName("bio") val bio: String? = null,
    
    // Stats
    @SerialName("follower_count") val followerCount: Int = 0,
    @SerialName("following_count") val followingCount: Int = 0,
    @SerialName("total_post_likes_count") val totalPostLikesCount: Int = 0,
    
    // Coins
    @SerialName("coin_wallet") val coinWallet: Int = 0,
    @SerialName("coin_collected_lifetime") val coinCollectedLifetime: Int = 0,
    @SerialName("coin_gifted_lifetime") val coinGiftedLifetime: Int = 0,
    @SerialName("coin_purchased_lifetime") val coinPurchasedLifetime: Int = 0,
    
    // Status
    @SerialName("is_verify") val isVerify: Boolean = false,
    @SerialName("is_freez") val isFreez: Boolean = false,
    @SerialName("is_readonly") val isReadOnly: Boolean? = null,
    @SerialName("show_feed_ads") val showFeedAds: Boolean = false,
    @SerialName("show_profile_ads") val showProfileAds: Boolean = false,
    @SerialName("is_moderator") val isModerator: Boolean = false,
    
    // Settings
    @SerialName("device") val device: String? = null,
    @SerialName("app_language") val appLanguage: String = "en",
    @SerialName("who_can_view_post") val whoCanViewPost: Int = 0,
    @SerialName("show_my_following") val showMyFollowing: Boolean = true,
    @SerialName("receive_message") val receiveMessage: Boolean = true,
    
    // Notifications
    @SerialName("notify_post_like") val notifyPostLike: Boolean = true,
    @SerialName("notify_post_comment") val notifyPostComment: Boolean = true,
    @SerialName("notify_follow") val notifyFollow: Boolean = true,
    @SerialName("notify_mention") val notifyMention: Boolean = true,
    @SerialName("notify_gift_received") val notifyGiftReceived: Boolean = true,
    @SerialName("notify_chat") val notifyChat: Boolean = true,
    @SerialName("notify_message_requests") val notifyMessageRequests: Boolean = true,

    // Location
    @SerialName("country") val country: String? = null,
    @SerialName("country_code") val countryCode: String? = null,
    @SerialName("region") val region: String? = null,
    @SerialName("city") val city: String? = null,
    @SerialName("lat") val lat: Double? = null,
    @SerialName("lon") val lon: Double? = null,
    
    // Relationship
    // Relationship
    @SerialName("is_following") val isFollowing: Boolean = false,
    @SerialName("has_active_story") val hasActiveStory: Boolean = false,
    @SerialName("follow_status") val followStatus: Int = 0,
    @SerialName("is_blocked") val isBlocked: Boolean = false,

    @SerialName("kashrut_level") val kashrutLevel: Int? = null,
    /** רמת כשרות תוכן (ליוצרים) 1–4. להצגה בפרופיל; אם null – לא להציג תגית. */
    @SerialName("artist_type") val artistType: Int? = null,
    @SerialName("consumption_category_ids") val consumptionCategoryIds: List<Int>? = null,
    @SerialName("upload_category_id") val uploadCategoryId: Int? = null,

    // External source IDs and auto-pull
    @SerialName("instagram_id") val instagramId: String? = null,
    @SerialName("whatsapp_status_id") val whatsappStatusId: String? = null,
    @SerialName("youtube_channel_id") val youtubeChannelId: String? = null,
    @SerialName("auto_pull_instagram") val autoPullInstagram: Boolean = false,
    @SerialName("auto_pull_whatsapp_status") val autoPullWhatsappStatus: Boolean = false,
    @SerialName("auto_pull_youtube") val autoPullYoutube: Boolean = false,

    /** When auto-pulling from source, also update profile photo and bio. */
    @SerialName("auto_update_profile_instagram") val autoUpdateProfileInstagram: Boolean = false,
    @SerialName("auto_update_profile_whatsapp_status") val autoUpdateProfileWhatsappStatus: Boolean = false,
    @SerialName("auto_update_profile_youtube") val autoUpdateProfileYoutube: Boolean = false,

    // Links
    @SerialName("links") val links: List<UserLinkDto>? = null,
    
    // Token
    @SerialName("token") val token: TokenDto? = null,
    
    // Timestamps
    @SerialName("created_at") val createdAt: String? = null,
    @SerialName("updated_at") val updatedAt: String? = null,
    @SerialName("app_last_used_at") val appLastUsedAt: String? = null,
    @SerialName("username_changed_at") val usernameChangedAt: String? = null,
)

@Serializable
data class UserLinkDto(
    @SerialName("id") val id: Int,
    @SerialName("title") val title: String,
    @SerialName("url") val url: String,
    @SerialName("link_type") val linkType: Int
)

@Serializable
data class TokenDto(
    @SerialName("auth_token") val authToken: String,
    @SerialName("firebase_token") val firebaseToken: String
)

@Serializable
data class LoginResponse(
    @SerialName("status") val status: Boolean = true,
    @SerialName("message") val message: String? = null,
    @SerialName("data") val data: LoginDataDto? = null
)

@Serializable
data class LoginDataDto(
    @SerialName("user") val user: UserDto? = null,
    @SerialName("access_token") val accessToken: String,
    @SerialName("refresh_token") val refreshToken: String,
    @SerialName("token_type") val tokenType: String,
    @SerialName("firebase_custom_token") val firebaseCustomToken: String? = null
)


@Serializable
data class FollowPreferencesDto(
    @SerialName("notify_new_posts") val notifyNewPosts: Boolean = true,
    @SerialName("notify_new_stories") val notifyNewStories: Boolean = true,
    @SerialName("notify_live") val notifyLive: Boolean = true
)

@Serializable
data class SessionsWrapper(
    @SerialName("sessions") val sessions: List<SessionDto> = emptyList()
)

@Serializable
data class SessionDto(
    @SerialName("id") val id: Int,
    @SerialName("is_hadran") val isHadran: Boolean,
    @SerialName("kosher_level_id") val kosherLevelId: Int? = null,
    @SerialName("last_seen_at") val lastSeenAt: String,
    @SerialName("device_id") val deviceId: String? = null,
    @SerialName("user_agent") val userAgent: String? = null,
    @SerialName("is_current") val isCurrent: Boolean = false
)

@Serializable
data class VerificationStartDto(
    @SerialName("phone_to_call") val phoneToCall: String,
    @SerialName("code") val code: String,
    @SerialName("expires_at") val expiresAt: String
)

@Serializable
data class VerificationStatusDto(
    @SerialName("status") val status: String? = null,
    @SerialName("code") val code: String? = null,
    @SerialName("phone_to_call") val phoneToCall: String? = null,
    @SerialName("expires_at") val expiresAt: String? = null,
    @SerialName("can_start") val canStart: Boolean = true,
    @SerialName("retry_days_remaining") val retryDaysRemaining: Int? = null
)

@Serializable
data class PaginationDto(
    @SerialName("limit") val limit: Int? = null,
    @SerialName("has_more") val hasMore: Boolean = false,
    @SerialName("last_id") val lastId: Int? = null,
    @SerialName("last_created_at") val lastCreatedAt: String? = null
)
